import { MLRequest, PolygonCoordinate } from '../../Annotation/annotation.types';
import { fabric } from 'fabric';
import { getUniqueId, fabricObjectProperties } from '../Helpers';
import { ValidationModalDto } from '../../Annotation/AnnotationValidationModal/AnnotationValidationModal.slice';
var inside = require('point-in-polygon');
var polygonsIntersect = require('polygons-intersect');

export type RawPoint = {
  x: number;
  y: number;
};

export const prepareMLrequest = (points: RawPoint[], scale: number): MLRequest => {
  const polygonCoordinates: PolygonCoordinate = {
    x: [],
    y: []
  };
  points.forEach(point => {
    polygonCoordinates.x.push(Math.trunc(point.x / scale));
    polygonCoordinates.y.push(Math.trunc(point.y / scale));
  });
  const req: MLRequest = {
    polygonCoordinates: polygonCoordinates
  };

  return req;
};

export const isPointInPolygone = (point: RawPoint, poloygone: RawPoint[]) => {
  const pointValues = [point.x, point.y];
  const poloygoneValues = poloygone.map(point => [point.x, point.y]);
  return inside(pointValues, poloygoneValues);
};

export const getPoints = (object: fabric.Rect | fabric.Object | fabric.Polygon): RawPoint[] => {
  if (object instanceof fabric.Rect) {
    return clearPointArray([object.aCoords!.tl, object.aCoords!.tr, object.aCoords!.br, object.aCoords!.bl]);
  }

  if (object instanceof fabric.Polygon) {
    return clearPointArray(object.get('points')!);
  }

  return [];
};

const clearPointArray = (points: fabric.Point[]): RawPoint[] => {
  return points.map(point => ({ x: point.x, y: point.y }));
};

export const generatePolygonPointCircle = (options: fabric.IEvent, canvasEl: fabric.Canvas, zoom: number, setRed: boolean) => {
  return new fabric.Circle({
    radius: 5 / zoom,
    fill: setRed ? 'red' : '#ffffff',
    stroke: '#333333',
    strokeWidth: 0.5,
    left: canvasEl!.getPointer(options.e).x,
    top: canvasEl!.getPointer(options.e).y,
    selectable: false,
    hasBorders: false,
    hasControls: false,
    originX: 'center',
    originY: 'center',
    name: getUniqueId(),
    objectCaching: false
  });
};

export const generatePolygonConnectLine = (options: fabric.IEvent, canvasEl: fabric.Canvas, zoom: number) => {
  const points = [
    canvasEl!.getPointer(options.e).x,
    canvasEl!.getPointer(options.e).y,
    canvasEl!.getPointer(options.e).x,
    canvasEl!.getPointer(options.e).y
  ];

  return new fabric.Line(points, {
    strokeWidth: 2 / zoom,
    fill: '#999999',
    stroke: '#999999',
    originX: 'center',
    originY: 'center',
    selectable: false,
    hasBorders: false,
    hasControls: false,
    evented: false,
    name: 'line',
    objectCaching: false
  });
};

export const generateRectFromPoint = (e: fabric.IEvent, canvasEl: fabric.Canvas) => {
  var pointer = canvasEl.getPointer(e.e);
  const origXNew = pointer.x;
  const origYNew = pointer.y;
  const id = getUniqueId();

  const newRect = new fabric.Rect({
    left: origXNew,
    top: origYNew,
    originX: 'left',
    originY: 'top',
    width: pointer.x - origXNew,
    height: pointer.y - origYNew,
    angle: 0,
    name: id,
    ...fabricObjectProperties
  });

  return {
    newRect,
    origXNew,
    origYNew
  };
};

export const generateIndicatorPolygon = (points: RawPoint[]) => {
  return new fabric.Polygon(points, {
    ...fabricObjectProperties,
    selectable: false,
    hasBorders: false,
    hasControls: false,
    evented: false,
    opacity: 0.3,
    objectCaching: false
  });
};

export const generateFinalPolygon = (points: RawPoint[]) => {
  return new fabric.Polygon(points, {
    ...fabricObjectProperties,
    name: getUniqueId()
  });
};

export const isPointInsideOverlap = (scaledOverlapping: RawPoint[][], point: RawPoint): boolean => {
  const pointInAnyOverlap = scaledOverlapping.some(overlap => {
    return isPointInPolygone(point, overlap);
  });

  return pointInAnyOverlap;
};

export const validateImageBoundaries = (annotationShape: RawPoint[], backgroundImage: any): boolean => {
  const cordinates = backgroundImage.aCoords;
  const cordinatesArray = [cordinates.tl, cordinates.tr, cordinates.br, cordinates.bl];
  const imagePolygone = cordinatesArray.map(point => ({ x: point.x, y: point.y }));
  const anyPointOutsideOfImage = annotationShape.some(point => {
    return !isPointInPolygone(point, imagePolygone);
  });
  const isValid = !anyPointOutsideOfImage;
  return isValid;
};

export const validateIntersection = (annotationShape: RawPoint[], scaledOverlapping: RawPoint[][]): boolean => {
  const anyIntersection = scaledOverlapping.some(overlap => {
    const intersectionResult: [] = polygonsIntersect(annotationShape, overlap);
    return intersectionResult.length > 0;
  });
  return anyIntersection === false;
};

export const validateOverlapping = (annotationShape: RawPoint[], scaledOverlapping: RawPoint[][]): boolean => {
  const noIntersection = validateIntersection(annotationShape, scaledOverlapping);
  const anyPointInsideOverlap = annotationShape.some(point => {
    return isPointInsideOverlap(scaledOverlapping, point);
  });
  return !anyPointInsideOverlap && noIntersection;
};

export const validateAnnotationShape = (annotationShape: RawPoint[], backgroundImage: any, scaledOverlapping: RawPoint[][]) => {
  const validBoundaries = validateImageBoundaries(annotationShape, backgroundImage);
  const validOverlap = validateOverlapping(annotationShape, scaledOverlapping);
  const isValid = validBoundaries && validOverlap;
  const result: ValidationModalDto = {
    showModal: false,
    isShapeValid: isValid,
    overlappingIssue: !validOverlap,
    outOfBoundariesIssue: !validBoundaries
  };
  return result;
};

export const getBackgroundImage = (canvasEl: fabric.Canvas) => {
  return canvasEl.getObjects('image')[0];
};

export const generateAndRenderObjectsForNewPoint = (
  canvasEl: fabric.Canvas,
  options: fabric.IEvent,
  pointArray: fabric.Circle[],
  activeShape: fabric.Polygon | null | undefined
) => {
  const zoom = canvasEl.getZoom();
  const circle = generatePolygonPointCircle(options, canvasEl, zoom, pointArray.length === 0);
  const line = generatePolygonConnectLine(options, canvasEl, zoom);

  let points;
  const clickPosition = canvasEl.getPointer(options.e);

  if (activeShape) {
    points = [
      ...activeShape!.get('points')!.map(p => ({
        x: p.x,
        y: p.y
      })),
      clickPosition
    ];

    canvasEl.remove(activeShape);
  } else {
    points = [clickPosition];
  }

  const polygon = generateIndicatorPolygon(points);
  canvasEl.add(polygon);
  canvasEl.add(line);
  canvasEl.add(circle);
  canvasEl.selection = false;
  canvasEl.renderAll();

  return {
    polygon,
    circle,
    line
  };
};

export const generateAndRenderPolygonObject = (
  canvasEl: fabric.Canvas,
  pointArray: fabric.Circle[],
  lineArray: fabric.Line[],
  activeLine: fabric.Line,
  activeShape: fabric.Polygon
) => {
  const points = new Array<RawPoint>();
  pointArray.forEach(point => {
    points.push({
      x: point.left as number,
      y: point.top as number
    });
    canvasEl.remove(point);
  });

  lineArray.forEach(line => {
    canvasEl.remove(line);
  });

  canvasEl.remove(activeShape).remove(activeLine);
  const polygon = generateFinalPolygon(points);
  canvasEl.add(polygon);
  canvasEl.renderAll();

  const polygonName = polygon.name!;

  canvasEl.selection = true;

  return {
    points,
    polygonName
  };
};

export const clearPolygonElementsFromCanvas = (
  canvasEl: fabric.Canvas,
  pointArray: fabric.Circle[],
  lineArray: fabric.Line[],
  activeLine: fabric.Line | undefined | null,
  activeShape: fabric.Polygon | null | undefined
) => {
  canvasEl!.remove(...pointArray, ...lineArray);
  if (activeLine) {
    canvasEl!.remove(activeLine);
  }
  if (activeShape) {
    canvasEl!.remove(activeShape);
  }
};

export const isOutsideOfObjects = (e: fabric.IEvent, polygonMode: boolean, rectangleMode: boolean) =>
  !polygonMode && !rectangleMode && ((e.target && !e.target.selectable) || !e.target);
export const onInitialPoint = (e: fabric.IEvent, polygonMode: boolean, pointArray: fabric.Circle[]) =>
  e.target && e.target.type === 'circle' && pointArray.length && e.target.name === pointArray[0].name && polygonMode;
export const isEmptyFieldInPolygonMode = (e: fabric.IEvent, polygonMode: boolean) =>
  !(e.target && (e.target.type === 'polygon' || e.target.type === 'circle')) && polygonMode;
export const canDrawRect = (rectangleMode: boolean, canvasEl: fabric.Canvas) => rectangleMode && !canvasEl.getActiveObject();

export const getModifiedObjectPoints = (obj: fabric.Object) => {
  let matrix = (obj as any).calcTransformMatrix();
  let transformedPoints = (obj as any)
    .get('points')
    .map((p: fabric.Point) => new fabric.Point(p.x - (obj as any).pathOffset.x, p.y - (obj as any).pathOffset.y))
    .map((p: any) => fabric.util.transformPoint(p, matrix));

  return clearPointArray(transformedPoints);
};

export const canPanCanvas = (isPanning: boolean, e: fabric.IEvent, canvasEl: fabric.Canvas, polygonMode: boolean, rectangleMode: boolean) =>
  isPanning && e && e.e && !canvasEl.getActiveObject() && !rectangleMode && !polygonMode;

export const moveRect = (canvasEl: fabric.Canvas, e: fabric.IEvent, origX: number | undefined, origY: number | undefined, rect: fabric.Rect) => {
  var pointer = canvasEl.getPointer(e.e);

  if (origX! > pointer.x) {
    rect.set({ left: pointer.x });
  }
  if (origY! > pointer.y) {
    rect.set({ top: pointer.y });
  }

  rect.set({ width: Math.abs(origX! - pointer.x) });
  rect.set({ height: Math.abs(origY! - pointer.y) });
};

export const updateEndLinePoint = (
  canvasEl: fabric.Canvas,
  e: fabric.IEvent,
  activeLine: fabric.Line,
  activeShape: fabric.Polygon | null | undefined,
  pointArray: fabric.Circle[]
) => {
  const pointer = canvasEl.getPointer(e.e);
  activeLine.set({ x2: pointer.x, y2: pointer.y });
  if (activeShape) {
    let points = activeShape.get('points')!;

    points[pointArray.length - 1] = new fabric.Point(pointer.x, pointer.y);

    activeShape.set({
      points: points
    });
    canvasEl.renderAll();
  }
};

export const makePolygonEditable = (polygon: fabric.Polygon, canvasEl: fabric.Canvas) => {
  (polygon as any).edit = true;
  var lastControl = polygon.points!.length - 1;
  polygon.objectCaching = false;
  polygon.transparentCorners = false;
  polygon.cornerStyle = 'circle';
  polygon.cornerSize = 10;
  polygon.cornerColor = 'rgba(0,0,255,0.5)';

  (polygon as any).controls = polygon.points!.reduce(function (acc: any, point, index) {
    acc['p' + index] = new (fabric as any).Control({
      positionHandler: function polygonPositionHandler(dim: any, finalMatrix: any, fabricObject: any) {
        var x = fabricObject.points[this.pointIndex].x - fabricObject.pathOffset.x,
          y = fabricObject.points[this.pointIndex].y - fabricObject.pathOffset.y;

        return fabric.util.transformPoint(
          new fabric.Point(x, y),
          fabric.util.multiplyTransformMatrices(canvasEl.viewportTransform!, fabricObject.calcTransformMatrix())
        );
      },
      actionHandler: anchorWrapper(index > 0 ? index - 1 : lastControl, actionHandler),
      actionName: 'modifyPolygon',
      pointIndex: index
    });
    return acc;
  }, {});
};

function actionHandler(eventData: any, transform: any, x: any, y: any) {
  var polygon = transform.target,
    currentControl = polygon.controls[polygon.__corner],
    mouseLocalPosition = polygon.toLocalPoint(new fabric.Point(x, y), 'center', 'center'),
    polygonBaseSize = polygon._getNonTransformedDimensions(),
    size = polygon._getTransformedDimensions(0, 0),
    finalPointPosition = {
      x: (mouseLocalPosition.x * polygonBaseSize.x) / size.x + polygon.pathOffset.x,
      y: (mouseLocalPosition.y * polygonBaseSize.y) / size.y + polygon.pathOffset.y
    };
  polygon.points[currentControl.pointIndex] = finalPointPosition;
  return true;
}

function anchorWrapper(anchorIndex: any, fn: any) {
  return function (eventData: any, transform: any, x: any, y: any) {
    var fabricObject = transform.target,
      absolutePoint = fabric.util.transformPoint(
        new fabric.Point(
          fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x,
          fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y
        ),
        fabricObject.calcTransformMatrix()
      ),
      actionPerformed = fn(eventData, transform, x, y),
      newDim = fabricObject._setPositionDimensions({}),
      polygonBaseSize = fabricObject._getNonTransformedDimensions(),
      newX = (fabricObject.points[anchorIndex].x - fabricObject.pathOffset.x) / polygonBaseSize.x,
      newY = (fabricObject.points[anchorIndex].y - fabricObject.pathOffset.y) / polygonBaseSize.y;
    fabricObject.setPositionByOrigin(absolutePoint, newX + 0.5, newY + 0.5);
    return actionPerformed;
  };
}
